import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, X } from 'lucide-react';

/**
 * Seleção múltipla por clique, sem digitação livre.
 *
 * Digitar valor de filtro é fonte de erro: a base tem grafias diferentes para
 * a mesma cidade e o usuário não sabe quais opções existem. Aqui as opções vêm
 * do backend com a contagem de contatos, o que também ajuda a decidir o recorte.
 *
 * @param opcoes  [{ valor, label, total }]
 * @param valores array de valores selecionados
 */
const SelecaoMultipla = ({
  opcoes = [],
  valores = [],
  onChange,
  placeholder = 'Todos',
  darkMode,
  titulo,
  larguraMinima = 170,
}) => {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const ref = useRef(null);

  // Fecha ao clicar fora, comportamento esperado de dropdown
  useEffect(() => {
    const fora = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAberto(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, []);

  const alternar = (valor) => {
    const novo = valores.includes(valor)
      ? valores.filter(v => v !== valor)
      : [...valores, valor];
    onChange(novo);
  };

  const visiveis = busca.trim()
    ? opcoes.filter(o => String(o.label).toLowerCase().includes(busca.trim().toLowerCase()))
    : opcoes;

  const rotulo = valores.length === 0
    ? placeholder
    : valores.length === 1
      ? (opcoes.find(o => o.valor === valores[0])?.label || valores[0])
      : `${valores.length} selecionadas`;

  const borda = darkMode ? 'border-zinc-700' : 'border-gray-300';
  const fundo = darkMode ? 'bg-zinc-900' : 'bg-white';
  const texto = darkMode ? 'text-white' : 'text-gray-900';
  const secundario = darkMode ? 'text-gray-400' : 'text-gray-500';

  return (
    <div className="relative" ref={ref} style={{ minWidth: larguraMinima }}>
      <button
        type="button"
        onClick={() => setAberto(a => !a)}
        title={titulo}
        className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg text-xs border ${borda} ${fundo} ${
          valores.length ? texto : secundario
        }`}
      >
        <span className="truncate">{rotulo}</span>
        <span className="flex items-center gap-1 shrink-0">
          {valores.length > 0 && (
            <X
              size={13}
              className={secundario}
              onClick={(e) => { e.stopPropagation(); onChange([]); }}
              title="Limpar seleção"
            />
          )}
          <ChevronDown size={13} className={secundario} />
        </span>
      </button>

      {aberto && (
        <div className={`absolute z-30 mt-1 w-full min-w-[240px] max-h-72 overflow-auto rounded-lg border shadow-lg ${borda} ${fundo}`}>
          {opcoes.length > 8 && (
            <div className={`sticky top-0 p-2 border-b ${borda} ${fundo}`}>
              <input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar nas opções"
                autoFocus
                className={`w-full px-2 py-1 rounded text-xs border ${borda} ${fundo} ${texto}`}
              />
            </div>
          )}

          {valores.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className={`w-full text-left px-3 py-1.5 text-xs ${secundario} hover:underline`}
            >
              Limpar {valores.length} selecionada(s)
            </button>
          )}

          {visiveis.length === 0 ? (
            <p className={`px-3 py-3 text-xs ${secundario}`}>Nenhuma opção encontrada</p>
          ) : visiveis.map(({ valor, label, total }) => {
            const marcado = valores.includes(valor);
            return (
              <button
                key={valor}
                type="button"
                onClick={() => alternar(valor)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs text-left ${
                  darkMode ? 'hover:bg-zinc-800' : 'hover:bg-gray-50'
                } ${marcado ? (darkMode ? 'bg-zinc-800' : 'bg-orange-50') : ''}`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                    marcado ? 'bg-orange-600 border-orange-600' : borda
                  }`}>
                    {marcado && <Check size={10} className="text-white" />}
                  </span>
                  <span className={`truncate ${texto}`}>{label}</span>
                </span>
                {total !== undefined && (
                  <span className={`shrink-0 ${secundario}`}>{Number(total).toLocaleString('pt-BR')}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SelecaoMultipla;
